import {useMemo} from 'react'
import {useNavigate} from 'react-router-dom'

import {KTSVG} from 'helpers'
import {markNotificationReadApi} from 'utils/api/auth/notificationService'
import {NotificationItem} from 'models/Notification'
import {formatDate, getTimeAgo} from 'utils/helper'
import {useTranslation} from 'react-i18next'

interface Props {
 open: boolean
 onClose: () => void
 notifications: NotificationItem[]
 refreshNotifications: () => Promise<void>
 unreadCount: number
}

type GroupKey =
 | 'Today'
 | 'Yesterday'
 | 'Last 7 Days'
 | 'Last 30 Days'
 | 'Earlier'

function getGroupKey(dateStr: string): GroupKey {
 const now = new Date()
 const date = new Date(dateStr)

 const today = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate()
 )

 const yesterday = new Date(today)
 yesterday.setDate(yesterday.getDate() - 1)

 const sevenDaysAgo = new Date(today)
 sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

 const thirtyDaysAgo = new Date(today)
 thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

 const itemDate = new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate()
 )

 if (itemDate.getTime() === today.getTime()) {
  return 'Today'
 }

 if (itemDate.getTime() === yesterday.getTime()) {
  return 'Yesterday'
 }

 if (itemDate >= sevenDaysAgo) {
  return 'Last 7 Days'
 }

 if (itemDate >= thirtyDaysAgo) {
  return 'Last 30 Days'
 }

 return 'Earlier'
}

const GROUP_ORDER: GroupKey[] = [
 'Today',
 'Yesterday',
 'Last 7 Days',
 'Last 30 Days',
 'Earlier',
]

export default function NotificationModal({
 open,
 onClose,
 notifications,
 refreshNotifications,
 unreadCount,
}: Props) {
 const navigate = useNavigate()
 const {t} = useTranslation()
 const grouped = useMemo(() => {
  const map: Partial<Record<GroupKey, NotificationItem[]>> =
   {}

  for (const item of notifications) {
   const key = getGroupKey(item.created_at)

   if (!map[key]) {
    map[key] = []
   }

   map[key]?.push(item)
  }

  return map
 }, [notifications])

 const handleMarkAllRead = async (unreadCount: number) => {
  if (unreadCount === 0) return

  const unreadIds = notifications
   .filter((item) => !item.is_read)
   .map((item) => item.id)

  if (!unreadIds.length) return

  const success = await markNotificationReadApi({
   app_code: 'web',
   mark_all_read: true,
  })

  if (success) {
   await refreshNotifications()
  }
 }

 if (!open) return null

 const onClickAction = (item: NotificationItem) => {
  if (!item.deep_link) return

  if (!item.is_read) {
   void markNotificationReadApi({
    app_code: 'web',
    notification_ids: [item.id],
   }).then(() => {
    void refreshNotifications()
   })
  }
  navigate(item.deep_link)

  onClose()
 }

 const getNotificationTime = (dateStr: string) => {
  const group = getGroupKey(dateStr)

  if (group === 'Today') {
   return getTimeAgo(dateStr)
  }

  return formatDate(dateStr)
 }

 return (
  <>
   <div
    className='fixed inset-0 bg-black/50 z-[99]'
    onClick={onClose}
   />

   <div className='absolute top-[78px] right-[120px] z-[100]'>
    <div className='relative bg-white w-[620px] rounded-[16px] shadow-xl border border-[#E6EBF0]'>
     <div className='absolute -top-[10px] right-[160px] w-[20px] h-[20px] bg-white rotate-45 border-l border-t border-[#E6EBF0] z-[1]' />
     <div className='rounded-[16px] overflow-hidden'>
      <div className='p-6'>
       <div className='flex items-center justify-between mb-6'>
        <div className='text-[20px] font-semibold text-[#181C32]'>
         {t('notification.title')}
        </div>
        <button
         onClick={async () =>
          handleMarkAllRead(unreadCount)
         }
         className={`text-[14px] font-semibold ${
          unreadCount === 0
           ? 'text-[#98A2B3] cursor-not-allowed'
           : 'text-[#4E83E4] cursor-pointer'
         }`}
        >
         {t('notification.markAllRead')}
        </button>
       </div>

       <div className='max-h-[500px] overflow-y-auto pr-1'>
        {notifications.length === 0 ? (
         <div className='text-center text-[#A1A5B7] py-10 text-[14px]'>
          {t('notification.noNotification')}
         </div>
        ) : (
         GROUP_ORDER.map((group) => {
          const items = grouped[group]
          if (!items || items.length === 0) return null

          return (
           <div key={group} className='mb-2'>
            <div className='text-[12px] font-semibold text-[#A1A5B7] uppercase tracking-wider mb-3 px-1'>
             {group}
            </div>

            {items.map((item) => (
             <div
              key={item.id}
              className={`rounded-[12px] border p-4 mb-3 flex justify-between gap-4 ${
               item.is_read
                ? 'bg-white border-[#E6EBF0]'
                : 'bg-[#F5F9FF] border-[#DCE8F8]'
              }`}
             >
              <div className='flex gap-3 flex-1'>
               <div className='relative'>
                {!item.is_read && (
                 <div className='absolute top-0 left-0 w-[8px] h-[8px] rounded-full bg-[#D92D20]' />
                )}
                <div
                 className={`w-[40px] h-[40px] rounded-full flex items-center justify-center ${
                  item.is_read
                   ? 'bg-[#F5F8FA]'
                   : 'bg-[#EAF3FF]'
                 }`}
                >
                 <KTSVG
                  path={
                   item.is_read
                    ? '/media/icons/read_notification.svg'
                    : '/media/icons/unread_notification.svg'
                  }
                  svgClassName='w-[20px] h-[20px]'
                 />
                </div>
               </div>

               <div className='flex-1'>
                <div className='text-[16px] font-semibold text-[#181C32]'>
                 {item.title}
                </div>
                <div className='text-[14px] text-[#5E6278] mt-1'>
                 {item.body}
                </div>
                {item.deep_link && (
                 <button
                  className='mt-3 border border-[#DCE1E7] rounded-lg px-4 py-2 text-primary text-[13px] font-medium'
                  onClick={() => onClickAction(item)}
                 >
                  {t('notification.viewDetails')}
                 </button>
                )}
               </div>
              </div>

              <div className='text-[12px] text-[#A1A5B7] whitespace-nowrap'>
               {getNotificationTime(item.created_at)}
              </div>
             </div>
            ))}
           </div>
          )
         })
        )}
       </div>
       <div className='pt-4 bg-white sticky bottom-0'>
        <button
         className='w-full border border-[#DCE1E7] rounded-xl py-4 text-[#4E83E4] text-[16px] font-semibold'
         onClick={() => {
          navigate('/notifications')
          onClose()
         }}
        >
         {t('notification.allNotifications')}
        </button>
       </div>
      </div>
     </div>
    </div>
   </div>
  </>
 )
}
